import { useMutation, useQuery } from "@apollo/client";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PlayArrow from "@mui/icons-material/PlayArrow";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LocalOffer from "@mui/icons-material/LocalOffer";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { LinearIcon } from "components/LinearIcon";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { WorkflowTagPickerAdapter } from "components/ManageTags/WorkflowTagPickerAdapter";
import { EntityUpdate } from "components/ManageTags/types";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import {
  ERROR_CLUSTER_RUN_ID_KEY,
  ERROR_CLUSTER_GROUP_ID_KEY,
} from "encamp-omnisearch/src/constants/keyNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  ActivityType,
  ErrorClusterGroupsByRunIdQuery,
  ErrorClusterGroupsByRunIdQueryVariables,
  ErrorClustersByRunIdQuery,
  ErrorClustersByRunIdQueryVariables,
  ErrorsBlockingCohortQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { prettyPrintDateTimeMed } from "util/dates";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/ManageWorkflowTags";

const QUEUE_ERROR_CLUSTERING_JOB_MUTATION = gql(`
  mutation QueueErrorClusteringJob($input: QueueErrorClusteringJobInput!) {
    queueErrorClusteringJob(input: $input) {
      id
      status
    }
  }
`);

const ERROR_CLUSTER_RUNS_QUERY = gql(`
  query ErrorClusterRuns($page: Int, $pageSize: Int) {
    errorClusterRuns(page: $page, pageSize: $pageSize) {
      count
      items {
        runId
        updatedAt
        groupCount
        totalErrors
      }
    }
  }
`);

const ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY = gql(`
  query ErrorClusterGroupsByRunId($runId: String!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    errorClusterGroupsByRunId(runId: $runId, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        groupId
        errorPattern
        groupSize
        facilitiesInGroupCount
        facilityIds
        workflowIds
        workflowTags
        untaggedWorkflowCount
        workflowsWithTags {
          workflowId
          tags
        }
        states
      }
    }
  }
`);

const ERRORS_BLOCKING_COHORT_QUERY = gql(`
  query ErrorsBlockingCohort($runId: String!) {
    errorsBlockingCohort(runId: $runId) {
      count
      items {
        workflowId
        cohortId
        groupId
        activityType
        cohortMembers
        errorMessage
      }
    }
  }
`);

const ERROR_CLUSTERS_BY_RUN_ID_QUERY = gql(`
  query ErrorClustersByRunId($runId: String!, $search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    errorClustersByRunId(runId: $runId, search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      count
      items {
        id
        workflowType
        runId
        groupId
        tenantId
        reportId
        facilityId
        workflowId
        activityId
        tenantName
        facilityName
        activityType
        activityDescription
        errorMessage
        reportTags
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
    }
  }
`);

const CREATE_LINEAR_ISSUE_MUTATION = gql(`
  mutation CreateLinearIssue($input: CreateLinearIssueInput!) {
    createLinearIssue(input: $input) {
      success
      issueIdentifier
      issueUrl
      issueId
    }
  }
`);

export type ErrorClusterGroupRow = NonNullable<
  NonNullable<
    ErrorClusterGroupsByRunIdQuery["errorClusterGroupsByRunId"]
  >["items"]
>[number];

export type ErrorClusterRow = NonNullable<
  NonNullable<ErrorClustersByRunIdQuery["errorClustersByRunId"]>["items"]
>[number];

export type ErrorBlockingCohortRow = NonNullable<
  NonNullable<ErrorsBlockingCohortQuery["errorsBlockingCohort"]>["items"]
>[number];

export function ErrorClustering() {
  const alerts = useAlerts();

  useBreadcrumb({
    label: "Error Triage",
    to: "/staff/fulfillment/error-clustering",
  });

  // Mutation for queuing error clustering job
  const [queueJob, { loading: queueJobLoading }] = useMutation(
    QUEUE_ERROR_CLUSTERING_JOB_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_RUNS_QUERY],
      onCompleted: () => {
        alerts.success("Error clustering job queued successfully!");
      },
      onError: (error) => {
        alerts.error("Failed to queue job", error);
      },
    }
  );

  const handleQueueJob = useCallback(() => {
    queueJob({
      variables: {
        input: {},
      },
    });
  }, [queueJob]);

  // Fetch available runs
  const { data: runsData, loading: runsLoading } = useQuery(
    ERROR_CLUSTER_RUNS_QUERY,
    {
      variables: {
        page: 0,
        pageSize: 100, // Get first 100 runs for the dropdown
      },
    }
  );

  // Default to the latest run (first in the list since ordered by updatedAt DESC)
  const latestRunId = runsData?.errorClusterRuns?.items?.[0]?.runId;
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>(
    latestRunId
  );

  // Update selected run when data loads
  useEffect(() => {
    if (latestRunId && !selectedRunId) {
      setSelectedRunId(latestRunId);
    }
  }, [latestRunId, selectedRunId]);

  // State for manage tags dialog
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [selectedGroupForTags, setSelectedGroupForTags] =
    useState<ErrorClusterGroupRow | null>(null);
  const [showTagsConfirmation, setShowTagsConfirmation] = useState(false);

  // Mutation for updating workflow tags
  const [updateWorkflowTags, { loading: updateTagsLoading }] = useMutation(
    UPDATE_WORKFLOW_TAGS_BULK_MUTATION,
    {
      refetchQueries: [ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY],
    }
  );

  // Mutation for creating Linear issues
  const [createLinearIssue, { loading: createLinearLoading }] = useMutation(
    CREATE_LINEAR_ISSUE_MUTATION
  );

  // Track which group is currently creating a Linear ticket (for loading state)
  const [creatingTicketForGroupId, setCreatingTicketForGroupId] = useState<
    number | null
  >(null);

  // State for Linear ticket confirmation dialog
  const [linearConfirmDialogOpen, setLinearConfirmDialogOpen] = useState(false);
  const [selectedGroupForLinear, setSelectedGroupForLinear] =
    useState<ErrorClusterGroupRow | null>(null);

  // Handle opening tags dialog for a group
  const handleOpenTagsDialog = useCallback((row: ErrorClusterGroupRow) => {
    setSelectedGroupForTags(row);
    setTagsDialogOpen(true);
  }, []);

  // Handle closing tags dialog
  const handleCloseTagsDialog = useCallback(() => {
    setTagsDialogOpen(false);
    setShowTagsConfirmation(false);
    setSelectedGroupForTags(null);
  }, []);

  // Handle submitting tag updates
  const handleSubmitTags = useCallback(
    async (updates: EntityUpdate[]) => {
      try {
        const workflowUpdates = updates.map((update) => ({
          workflowId: update.id,
          tagNames: update.tags,
        }));

        if (workflowUpdates.length > 0) {
          await updateWorkflowTags({
            variables: {
              workflowUpdates,
            },
          });
        }

        const workflowCount = workflowUpdates.length;
        alerts.success(
          `Successfully updated tags for ${workflowCount} ${pluralize(
            "workflow",
            workflowCount
          )}`
        );

        handleCloseTagsDialog();
      } catch (err) {
        alerts.error("An error occurred while updating the workflow tags", err);
      }
    },
    [updateWorkflowTags, alerts, handleCloseTagsDialog]
  );

  // Transform selected group workflows to dialog format with per-workflow tags
  const tagsDialogRows = useMemo(() => {
    if (!selectedGroupForTags) return [];
    return selectedGroupForTags.workflowsWithTags.map((workflow) => ({
      id: workflow.workflowId,
      tags: workflow.tags,
    }));
  }, [selectedGroupForTags]);

  // Fetch errors blocking cohorts
  const { data: blockingCohortData, loading: blockingCohortLoading } = useQuery(
    ERRORS_BLOCKING_COHORT_QUERY,
    {
      variables: {
        runId: selectedRunId ?? "",
      },
      skip: !selectedRunId,
    }
  );

  const handleRunChange = useCallback((runId: string) => {
    setSelectedRunId(runId);
  }, []);

  const [blockingCohortSortModel, setBlockingCohortSortModel] =
    useState<GridSortModel>([{ field: "cohortMembers", sort: "desc" }]);

  const blockingCohortColumns = useMemo(
    (): CustomGridColDef<ErrorBlockingCohortRow>[] => [
      {
        field: "groupId",
        headerName: "Group ID",
        width: 120,
        sortable: true,
      },
      {
        field: "activityType",
        headerName: "Activity Type",
        width: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {prettyPrintEnumValue(row.activityType)}
          </Typography>
        ),
      },
      {
        field: "cohortMembers",
        headerName: "Cohort Members",
        width: 150,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.cohortMembers}
          </Typography>
        ),
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1,
        minWidth: 300,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.errorMessage}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorMessage}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={`/staff/cohorts/${row.cohortId}`}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            endIcon={<OpenInNew />}
          >
            View Cohort
          </Button>
        ),
      },
    ],
    []
  );

  // Open the Linear ticket confirmation dialog
  const handleCreateLinearTicket = useCallback((row: ErrorClusterGroupRow) => {
    setSelectedGroupForLinear(row);
    setLinearConfirmDialogOpen(true);
  }, []);

  // Close the Linear ticket confirmation dialog
  const handleCloseLinearConfirmDialog = useCallback(() => {
    setLinearConfirmDialogOpen(false);
    setSelectedGroupForLinear(null);
  }, []);

  // Build the ticket title for display and submission
  const buildTicketInfo = useCallback(
    (row: ErrorClusterGroupRow) => {
      const omnisearch = encodeURIComponent(
        `${ERROR_CLUSTER_RUN_ID_KEY}:${selectedRunId} ${ERROR_CLUSTER_GROUP_ID_KEY}:${row.groupId}`
      );
      const viewWorkflowsUrl = `${window.location.origin}/staff/fulfillment/workflows?omnisearch=${omnisearch}`;

      const sortedStates = [...row.states].sort();
      const statesDisplay =
        sortedStates.length <= 3 ? sortedStates.join(", ") : "Multiple States";
      const truncatedPattern =
        row.errorPattern.length > 25
          ? `${row.errorPattern.slice(0, 25)}...`
          : row.errorPattern;
      const title = `[${row.groupSize}: ${statesDisplay}] Fulfillment Error: ${truncatedPattern}`;
      const description = [
        `**Error Pattern**: ${row.errorPattern}`,
        "",
        `**View Workflows**: ${viewWorkflowsUrl}`,
        "",
        `**Group Size**: ${row.groupSize}`,
        "",
        `**States**: ${sortedStates.join(", ")}`,
      ].join("\n");

      return { title, description, sortedStates, viewWorkflowsUrl };
    },
    [selectedRunId]
  );

  // Actually create the Linear ticket after confirmation
  const confirmCreateLinearTicket = useCallback(async () => {
    if (!selectedGroupForLinear) return;

    const row = selectedGroupForLinear;
    const { title, description } = buildTicketInfo(row);

    setCreatingTicketForGroupId(row.groupId);

    try {
      // Create the Linear issue via API
      const result = await createLinearIssue({
        variables: {
          input: {
            title,
            description,
          },
        },
      });

      const issueIdentifier = result.data?.createLinearIssue?.issueIdentifier;
      const issueUrl = result.data?.createLinearIssue?.issueUrl;

      if (!issueIdentifier) {
        throw new Error("Failed to get issue identifier from Linear");
      }

      alerts.success(`Linear ticket ${issueIdentifier} created successfully!`);

      // Tag all workflows in the group with the ticket ID and "engineering" tag
      const workflowUpdates = row.workflowsWithTags.map((workflow) => ({
        workflowId: workflow.workflowId,
        tagNames: [
          ...new Set([...workflow.tags, issueIdentifier, "engineering"]),
        ],
      }));

      if (workflowUpdates.length > 0) {
        await updateWorkflowTags({
          variables: {
            workflowUpdates,
          },
        });

        alerts.success(
          `Tagged ${workflowUpdates.length} ${pluralize(
            "workflow",
            workflowUpdates.length
          )} with ${issueIdentifier}`
        );
      }

      // Open the issue in a new tab
      if (issueUrl) {
        window.open(issueUrl, "_blank", "noopener");
      }

      // Close the dialog
      handleCloseLinearConfirmDialog();
    } catch (err) {
      alerts.error("Failed to create Linear ticket", err);
    } finally {
      setCreatingTicketForGroupId(null);
    }
  }, [
    selectedGroupForLinear,
    buildTicketInfo,
    createLinearIssue,
    updateWorkflowTags,
    alerts,
    handleCloseLinearConfirmDialog,
  ]);

  const groupedColumns = useMemo(
    (): OmnisearchGridColDef<ErrorClusterGroupRow>[] => [
      {
        field: "groupId",
        headerName: "Group ID",
        width: 120,
        sortable: true,
      },
      {
        field: "errorPattern",
        headerName: "Error Pattern",
        flex: 1,
        minWidth: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.errorPattern}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorPattern}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "groupSize",
        headerName: "Group Size",
        width: 130,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.groupSize}
          </Typography>
        ),
      },
      {
        field: "workflowTags",
        headerName: "Tags",
        width: 200,
        sortable: false,
        renderCell: ({ row }) => {
          if (row.workflowTags.length === 0) return null;

          // Sort alphabetically and determine how many to show
          const sortedTags = [...row.workflowTags].sort();
          // Show max 3 tags, then overflow if more
          const maxVisibleTags = 3;
          const hasOverflow = sortedTags.length > maxVisibleTags;
          const visibleTags = hasOverflow
            ? sortedTags.slice(0, maxVisibleTags)
            : sortedTags;
          const overflowCount = sortedTags.length - maxVisibleTags;

          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                overflow: "hidden",
                width: "100%",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenTagsDialog(row);
              }}
            >
              {visibleTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    flexShrink: 0,
                    maxWidth: 80,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              ))}
              {hasOverflow && (
                <Chip
                  label={`+${overflowCount}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    flexShrink: 0,
                    minWidth: "auto",
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "untaggedWorkflowCount",
        headerName: "Untagged",
        width: 100,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => (
          <Typography variant="body2" fontWeight="medium">
            {row.untaggedWorkflowCount}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 130,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const omnisearch = encodeURIComponent(
            `${ERROR_CLUSTER_RUN_ID_KEY}:${selectedRunId} ${ERROR_CLUSTER_GROUP_ID_KEY}:${row.groupId}`
          );
          const isCreatingTicket = creatingTicketForGroupId === row.groupId;

          return (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                opacity: 0,
                transition: "opacity 0.3s ease",
                ".MuiDataGrid-row:hover &": {
                  opacity: 1,
                },
              }}
            >
              <Tooltip title="Manage Tags">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenTagsDialog(row);
                  }}
                  aria-label="Manage Tags"
                >
                  <LocalOffer fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Workflows">
                <IconButton
                  size="small"
                  component={Link}
                  to={`/staff/fulfillment/workflows?omnisearch=${omnisearch}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View Workflows"
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Create Linear Ticket">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateLinearTicket(row);
                  }}
                  disabled={isCreatingTicket || createLinearLoading}
                  aria-label="Create Linear Ticket"
                >
                  {isCreatingTicket ? (
                    <CircularProgress size={16} />
                  ) : (
                    <LinearIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      selectedRunId,
      handleOpenTagsDialog,
      handleCreateLinearTicket,
      creatingTicketForGroupId,
      createLinearLoading,
    ]
  );

  const columns = useMemo(
    (): OmnisearchGridColDef<ErrorClusterRow>[] => [
      {
        field: "groupId",
        headerName: "Group ID",
        width: 100,
        sortable: true,
      },
      {
        field: "workflowType",
        headerName: "Workflow Type",
        width: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {prettyPrintEnumValue(row.workflowType)}
          </Typography>
        ),
      },
      {
        field: "tenantName",
        headerName: "Tenant",
        width: 180,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.tenantName}>
            <Box>
              <TruncateTypography variant="body2">
                {row.tenantName}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "facilityName",
        headerName: "Facility",
        width: 180,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.facilityName}>
            <Box>
              <TruncateTypography variant="body2">
                {row.facilityName}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "activityType",
        headerName: "Activity Type",
        width: 200,
        sortable: true,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ActivityType),
        renderCell: ({ row }) => (
          <Typography variant="body2">
            {prettyPrintEnumValue(row.activityType)}
          </Typography>
        ),
      },
      {
        field: "activityDescription",
        headerName: "Activity Description",
        width: 200,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.activityDescription ?? ""}>
            <Box>
              <TruncateTypography variant="body2">
                {row.activityDescription ?? "-"}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "errorMessage",
        headerName: "Error Message",
        flex: 1,
        minWidth: 300,
        sortable: true,
        renderCell: ({ row }) => (
          <Tooltip title={row.errorMessage}>
            <Box>
              <TruncateTypography variant="body2">
                {row.errorMessage}
              </TruncateTypography>
            </Box>
          </Tooltip>
        ),
      },
      {
        field: "reportTags",
        headerName: "Report Tags",
        width: 180,
        sortable: false,
        renderCell: ({ row }) => (
          <Box display="flex" gap={0.5} flexWrap="wrap">
            {row.reportTags.slice(0, 2).map((tag, idx) => (
              <Chip key={idx} label={tag} size="small" variant="outlined" />
            ))}
            {row.reportTags.length > 2 && (
              <Chip
                label={`+${row.reportTags.length - 2}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        ),
      },
    ],
    []
  );

  if (runsLoading) {
    return (
      <Stack py={3}>
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  // Handle case where there are no runs
  if (!runsData?.errorClusterRuns?.items?.length) {
    return (
      <Stack p={2}>
        <Paper elevation={0} sx={{ p: 3 }}>
          <Typography variant="h5" component="h5" color="text.primary" pb={1}>
            Error Triage Dashboard
          </Typography>
          <Typography fontSize={12} component="p" pb={3}>
            View and analyze errors from workflow activities
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Typography color="text.secondary">
              No error clustering runs found. Run an error group analysis to get
              started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={handleQueueJob}
              disabled={queueJobLoading}
            >
              {queueJobLoading ? "Queueing..." : "Run Error Group Analysis"}
            </Button>
          </Box>
        </Paper>
      </Stack>
    );
  }

  // Handle case where runs exist but no run is selected yet
  if (!selectedRunId) {
    return (
      <Stack py={3}>
        <Typography>Loading...</Typography>
      </Stack>
    );
  }

  const selectedRun = runsData?.errorClusterRuns?.items.find(
    (run) => run.runId === selectedRunId
  );

  return (
    <Stack p={2}>
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          pb: 3,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} pb={1}>
                <Typography variant="h5" component="h5" color="text.primary">
                  Error Triage Dashboard
                </Typography>
              </Box>
              <Typography fontSize={12} component="p">
                View and analyze errors from workflow activities
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <FormControl sx={{ flex: 1, maxWidth: 600 }}>
                <InputLabel id="run-select-label">Runs</InputLabel>
                <Select
                  labelId="run-select-label"
                  value={selectedRunId}
                  label="Clustering Run"
                  onChange={(e) => handleRunChange(e.target.value)}
                >
                  {runsData?.errorClusterRuns?.items.map((run) => (
                    <MenuItem key={run.runId} value={run.runId}>
                      <Box>
                        <Typography variant="body1">
                          {prettyPrintDateTimeMed(
                            new Date(run.updatedAt).toISOString()
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {run.groupCount} groups • {run.totalErrors} errors •
                          Run ID: {run.runId.slice(0, 8)}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleQueueJob}
                disabled={queueJobLoading}
                sx={{ mt: 1 }}
              >
                {queueJobLoading ? "Queueing..." : "Run Error Group Analysis"}
              </Button>
            </Box>

            {selectedRun && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Showing {selectedRun.groupCount} error groups with{" "}
                  {selectedRun.totalErrors} total errors
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, px: 3 }}>
            <Typography variant="h6">Error Groups</Typography>
            <Tooltip title="Workflow activity errors grouped by semantic similarity of the error message.">
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <OmnisearchDataGrid<
            ErrorClusterGroupsByRunIdQuery,
            ErrorClusterGroupsByRunIdQueryVariables,
            ErrorClusterGroupRow
          >
            persistenceKey="error-clustering-groups-table"
            isURLDriven={false}
            columns={groupedColumns}
            dataQuery={ERROR_CLUSTER_GROUPS_BY_RUN_ID_QUERY}
            dataQueryVariables={{ runId: selectedRunId }}
            initialSortModel={[
              { field: "groupSize", sort: "desc" },
              { field: "groupId", sort: "asc" },
            ]}
            getItems={(data) => data.errorClusterGroupsByRunId?.items ?? []}
            getCount={(data) => data.errorClusterGroupsByRunId?.count ?? 0}
            getRowId={(row) => row.groupId}
            noDataMessage="No grouped error clusters found for this run."
            excludeFilterColumns={[
              "groupId",
              "facilitiesInGroupCount",
              "workflowTags",
              "untaggedWorkflowCount",
              "actions",
            ]}
            additionalFilterColumns={[
              {
                key: "errorPattern",
                header: "Error Pattern",
                filterKeyType: "text",
              },
            ]}
            initialPageSize={25}
            rowHeight="auto"
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, px: 3 }}>
            <Typography variant="h6">All Errors</Typography>
            <Tooltip title="Complete list of activity errors along with relevant metadata. The individual error messages shown here are the input to our clustering/grouping analysis used to create the error groups above.">
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <OmnisearchDataGrid<
            ErrorClustersByRunIdQuery,
            ErrorClustersByRunIdQueryVariables,
            ErrorClusterRow
          >
            persistenceKey="error-clustering-table"
            isURLDriven={false}
            columns={columns}
            dataQuery={ERROR_CLUSTERS_BY_RUN_ID_QUERY}
            dataQueryVariables={{ runId: selectedRunId }}
            initialSortModel={[{ field: "groupId", sort: "asc" }]}
            getItems={(data) => data.errorClustersByRunId?.items ?? []}
            getCount={(data) => data.errorClustersByRunId?.count ?? 0}
            noDataMessage="No error clusters found for this run."
            excludeFilterColumns={[
              "groupId",
              "reportTags",
              "activityDescription",
            ]}
            additionalFilterColumns={[
              {
                key: "workflowType",
                header: "Workflow Type",
                filterKeyType: "text",
              },
              {
                key: "tenantName",
                header: "Tenant",
                filterKeyType: "text",
              },
              {
                key: "facilityName",
                header: "Facility",
                filterKeyType: "text",
              },
              {
                key: "activityType",
                header: "Activity Type",
                filterKeyType: "enumAutocomplete",
              },
              {
                key: "errorMessage",
                header: "Error Message",
                filterKeyType: "text",
              },
            ]}
            initialPageSize={25}
            rowHeight="auto"
          />
        </Box>

        <Box sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, px: 3 }}>
            <Typography variant="h6">Error Groups Blocking Cohorts</Typography>
            <Tooltip title="Error groups that are preventing entire cohorts from progressing. Shows the activity type, number of affected cohort members, and links to view the cohort details.">
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <Box sx={{ px: 3 }}>
            <DataGrid<ErrorBlockingCohortRow>
              columns={blockingCohortColumns}
              rows={blockingCohortData?.errorsBlockingCohort?.items ?? []}
              loading={blockingCohortLoading}
              getRowId={(row) => `${row.workflowId}-${row.groupId}`}
              noRowsMessage="No errors blocking cohorts found for this run."
              autoHeight
              pagination
              paginationMode="client"
              sortingMode="client"
              sortModel={blockingCohortSortModel}
              onSortModelChange={setBlockingCohortSortModel}
            />
          </Box>
        </Box>
      </Paper>

      {/* Manage Tags Dialog */}
      <ManageTagsFormDialog
        selectedRows={tagsDialogRows}
        getEntityId={(row) => row.id}
        onClose={handleCloseTagsDialog}
        onCloseConfirmation={() => setShowTagsConfirmation(false)}
        confirm={() => setShowTagsConfirmation(true)}
        open={tagsDialogOpen}
        withConfirmation={true}
        showConfirmation={showTagsConfirmation}
        onSubmit={handleSubmitTags}
        loading={updateTagsLoading}
        entityName="Workflow"
        localStorageKey="error-clustering-workflow-tags"
        commonTags={["compliance", "engineering", "linear"]}
        TagPickerComponent={WorkflowTagPickerAdapter}
      />

      {/* Linear Ticket Confirmation Dialog */}
      <ConfirmDialog
        open={linearConfirmDialogOpen}
        onClose={handleCloseLinearConfirmDialog}
        onConfirm={confirmCreateLinearTicket}
        header="Create Linear Ticket"
        confirmText="Create Linear Ticket"
        cancelText="Cancel"
        loading={creatingTicketForGroupId !== null}
        confirmStartIcon={<LinearIcon />}
      >
        {selectedGroupForLinear && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              A Linear ticket will be created in the Engineering Support project
              with the following information:
            </Typography>
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Title
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 2, fontFamily: "monospace" }}
              >
                {buildTicketInfo(selectedGroupForLinear).title}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Error Pattern
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedGroupForLinear.errorPattern}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Details
              </Typography>
              <Typography variant="body2">
                Group Size: {selectedGroupForLinear.groupSize} • States:{" "}
                {[...selectedGroupForLinear.states].sort().join(", ")}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              After creation, all {selectedGroupForLinear.workflowIds.length}{" "}
              {pluralize("workflow", selectedGroupForLinear.workflowIds.length)}{" "}
              in this group will be tagged with the ticket ID and "engineering".
            </Typography>
          </Stack>
        )}
      </ConfirmDialog>
    </Stack>
  );
}
